import { Dimensions, PixelRatio, Platform, StyleSheet } from 'react-native';

import { isTizen, isWeb } from '../helpers/Platform';

// Get pixel ratio
export const pixelRatio = isWeb ? 1 : PixelRatio.get();

// Screen size
export const height = Dimensions.get(isTizen ? 'screen' : 'window').height * pixelRatio;
export const width = Dimensions.get(isTizen ? 'screen' : 'window').width * pixelRatio;

// Adjusts size to fit current resolution, assuming 1080p target resolution
const pixel = (size: number) => (size * (height / 1080)) / pixelRatio;

// Adjusted app size
export const appHeight = pixel(1080);
export const appWidth = pixel(1920);

export const Screen = {
  height,
  width,
};

const whisperWhite = '#ebebeb';
const malibuBlue = '#64afd6';

export const Theme = {
  white: '#ffffff',
  mainText: '#ebebeb',
  secondaryText: '#64afd6',
  errorText: '#fc0606',
  enabledColor: '#ebebeb',
  errorBackgroundColor: '#273e4e',
  disabledColor: `${'#ebebeb'}99`,
  legendBarColor: `${'#ebebeb'}80`,
  progressBarColor: '#ebebeb',
  progressBarMarkColor: '#ffffff33',
  loading: '#1caaf2',
  severeAlertColor: '#D1002F',
  black: '#000000',
  suvaGrey: '#8d8d8d',
  whisperWhite,
  dodgerLightBlue: '#1caaf2',
  malibuBlue,
  blackPearlBlue: '#151e2a',
  darkCeruleanBlue: '#064980',
  pacificBlue: '#168FCC',
  rhinoGrey: '#414851',
  blackPearlBlack: '#131A23',
  dodgerBlue: '#279CEA',
  azureWhite: '#feffff',
  alertRed: '#ff0f34',
  neroBlack: '#1a1a1a',
  curiousBlue: '#1F7BB8',
  red: '#fc0606',
  colorFocusText: '#243346',
  vodEnabled: '#56A9F1',
  vodDisabled: '#AEB9C1',
  lightBlue: '#BFDBEA',
  seagullBlue: '#75ACCC',
  alertCrawlRed: '#b22222',
  transparent: 'transparent',
  gradients: {
    alertModal: {
      start: '#151E2A00',
      end: '#151E2A',
    },
    vodBackground: {
      start: '#151E2A00',
      end: '#151E2A',
    },
    alertTile: {
      start: '#900024',
      end: '#CF0034',
    },
    anfTile: {
      start: '#343538',
      end: '#222226',
    },
    background: {
      start: '#102B40',
      end: '#0A1D29',
    },
    loading: {
      start: '#26364A',
      end: '#0C1117',
    },
    onboarding: {
      start: '#27364900',
      end: '#233245',
    },
    ldlPollenScale: {
      first: '#90F336',
      second: '#F4D736',
      third: '#F16322',
      fourth: '#EB001B',
      fifth: '#D900A6',
      sixth: '#70001B',
    },
    tile: {
      start: '#064980',
      end: '#168FCC',
    },
    deselectedTile: {
      start: '#ebebeb33',
      end: '#ebebeb33',
    },
    drawerModalBackground: {
      start: '#168ECB',
      end: '#074980',
    },
    settingsBackground: {
      first: '#ffffff',
      second: '#273649',
      third: '#273548',
      fourth: '#0e1319',
    },
    settingsContent: {
      start: '#168FCC',
      end: '#064980',
    },
  },
  switch: {
    activeColor: '#24334633',
    focusColor: '#243346',
    inactiveColor: '#24334633',
  },
  appWidth,
  appHeight,
  px: pixel,
  tvMarginHorizontal: () => pixel(90),
  withOpacity: (color: string, opacity: number) => {
    const alpha = Math.round(opacity * 255).toString(16);

    return alpha.length < 2 ? `${color}0${alpha}` : `${color}${alpha}`;
  },
  tvMarginVertical: () => pixel(30),
  pixelRatio: () => pixelRatio,
  tileBorderRadius: pixel(12),
  tileBorderWidth: pixel(6),
  sidePanelWidth: pixel(1180),
  styles: StyleSheet.create({
    border: {
      borderColor: whisperWhite,
      borderRadius: pixel(12) + pixel(6),
      borderWidth: pixel(6),
      padding: 0,
      ...Platform.select({
        web: {
          outlineWidth: 0,
        },
      }),
    },
    center: {
      alignItems: 'center',
      flex: 1,
      justifyContent: 'center',
    },
    header: {
      alignItems: 'center',
      height: pixel(300),
      justifyContent: 'center',
      width: pixel(1920),
    },
    headerText: {
      color: whisperWhite,
      fontSize: pixel(30),
    },
    right: {
      alignItems: 'center',
      backgroundColor: malibuBlue,
      flex: 1,
      flexDirection: 'column',
      height: pixel(1080),
      justifyContent: 'center',
      width: pixel(1920),
    },
    topCenter: {
      alignItems: 'center',
      flex: 1,
      justifyContent: 'flex-start',
    },
  }),
};
